/**
 * Accountant Export Center
 * =========================
 * Unified download hub for every per-case export. Collapses what used
 * to be four separate entry points — the Reconciliation page's inline
 * "Export Work Paper" button, two workbook buttons on a now-deleted
 * dashboard, and an audit-trail endpoint that had no UI — into one
 * route: `/accountant/case/:reportId/export`.
 *
 * Task 6 of docs/plans/2026-04-22-accountant-workspace-consolidation.md.
 * No backend changes — every download calls a pre-existing endpoint.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  FileSpreadsheet,
  FileText,
  Download,
  ShieldCheck,
  Loader2,
  AlertCircle,
  Filter,
  Archive,
} from 'lucide-react';

import AccountantLayout from '../components/AccountantLayout';
import api from '../utils/api';

type ExportId = 'reconciliation' | 'wb2' | 'wb1' | 'expense_worksheet' | 'audit_trail';

// Task 9 — filter controls for the Reconciliation Workpaper card.
// Other exports are whole-case by design and ignore these filters.
interface ReconciliationFilters {
  year: string;
  month: string;
  lineItem: string;
  source: string;
  format: 'xlsx' | 'csv';
}

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'bank_statements', label: 'Bank Statements' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'tax_returns', label: 'Tax Returns' },
  { value: 'pl_statements', label: 'P&L Statements' },
  { value: 'quickbooks', label: 'QuickBooks' },
] as const;

interface ExportCard {
  id: ExportId;
  title: string;
  format: string;
  description: string;
  icon: React.ReactNode;
  /** Build the backend URL for this export, scoped to the current report. */
  url: (reportId: string) => string;
  /** MIME type used to wrap the downloaded blob. */
  mime: string;
  /** Extension used in the Save As filename. */
  extension: 'xlsx' | 'json';
}

const EXPORT_CARDS: ExportCard[] = [
  {
    id: 'reconciliation',
    title: 'Reconciliation Workpaper',
    format: 'XLSX · 6 sheets',
    description:
      'P&L grid, transaction detail, anomalies, audit trail, variance analysis, sign-off. ' +
      'Apply the filters below for a court-exhibit-ready slice.',
    icon: <FileSpreadsheet size={22} />,
    // This URL is overridden at render time when Task 9 filters are set.
    url: (id) => `/api/v1/reconciliation/export/${id}`,
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx',
  },
  {
    id: 'wb2',
    title: 'BI Forensic Package',
    format: 'WB2 · XLSX · 13 tabs',
    description:
      'Full damages narrative workbook — the court-ready export carriers expect.',
    icon: <FileSpreadsheet size={22} />,
    url: (id) => `/api/v1/workbook/wb2/${id}`,
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx',
  },
  {
    id: 'wb1',
    title: 'Operating P&L',
    format: 'WB1 · XLSX · 8 tabs',
    description:
      'Standalone operating P&L for pre-event years. Feeds attorney demand packages.',
    icon: <FileSpreadsheet size={22} />,
    url: (id) => `/api/v1/workbook/wb1/${id}`,
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx',
  },
  {
    id: 'expense_worksheet',
    title: 'Continuing Expense Worksheet',
    format: 'XLSX · ISO CP 00 30 · 3 sheets',
    description:
      'ISO CP 00 30 continuing-expense ledger — summary + totals, one row per ' +
      'line item, and the BI-period source transactions backing each documented line.',
    icon: <FileSpreadsheet size={22} />,
    url: (id) => `/api/v1/expense-ledger/${id}/xlsx`,
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx',
  },
  {
    id: 'audit_trail',
    title: 'Audit Trail',
    format: 'JSON · hash-chained',
    description:
      'Immutable reconciliation history with SHA-256 chain. One record per accountant action.',
    icon: <ShieldCheck size={22} />,
    url: (id) => `/api/v1/audit/claim/${id}/export`,
    mime: 'application/json',
    extension: 'json',
  },
];

function filenameFor(
  card: ExportCard,
  reportId: string,
  extension: string = card.extension,
): string {
  const shortId = reportId.slice(0, 8);
  const today = new Date().toISOString().split('T')[0];
  const slug = card.id.replace(/_/g, '-');
  return `${slug}_${shortId}_${today}.${extension}`;
}

interface CardState {
  status: 'idle' | 'downloading' | 'done' | 'error';
  error?: string;
}

export default function AccountantExportCenter() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();

  const [cardState, setCardState] = useState<Record<ExportId, CardState>>({
    reconciliation: { status: 'idle' },
    wb2: { status: 'idle' },
    wb1: { status: 'idle' },
    expense_worksheet: { status: 'idle' },
    audit_trail: { status: 'idle' },
  });

  const [filters, setFilters] = useState<ReconciliationFilters>({
    year: '',
    month: '',
    lineItem: '',
    source: '',
    format: 'xlsx',
  });

  // Task 10 — Bates exhibit package state.
  const [batesPrefix, setBatesPrefix] = useState('CLM-');
  const [batesStart, setBatesStart] = useState('1');
  const [batesIncludeWorkpaper, setBatesIncludeWorkpaper] = useState(true);
  const [batesStatus, setBatesStatus] = useState<CardState>({ status: 'idle' });

  const reconciliationQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.year) params.set('year', filters.year);
    if (filters.month) params.set('month', filters.month);
    if (filters.lineItem) params.set('line_item', filters.lineItem);
    if (filters.source) params.set('source', filters.source);
    if (filters.format === 'csv') params.set('format', 'csv');
    const q = params.toString();
    return q ? `?${q}` : '';
  }, [filters]);

  const filtersActive =
    !!filters.year ||
    !!filters.month ||
    !!filters.lineItem ||
    !!filters.source ||
    filters.format === 'csv';

  if (!reportId) {
    return (
      <AccountantLayout>
        <div className="p-6">
          <div
            className="cs-body rounded-lg p-4"
            style={{
              background: 'var(--color-needs-human-tint)',
              border: '1px solid var(--color-needs-human-tint-bd)',
              color: 'var(--color-needs-human-text)',
            }}
          >
            <AlertCircle className="inline mr-2" size={18} />
            No report ID in URL. Open Export Center from a case page.
          </div>
        </div>
      </AccountantLayout>
    );
  }

  function effectiveUrl(card: ExportCard): string {
    const base = card.url(reportId!);
    if (card.id === 'reconciliation' && reconciliationQuery) {
      return `${base}${reconciliationQuery}`;
    }
    return base;
  }

  function effectiveExtension(card: ExportCard): string {
    if (card.id === 'reconciliation' && filters.format === 'csv') return 'csv';
    return card.extension;
  }

  async function handleBatesPackage() {
    setBatesStatus({ status: 'downloading' });
    try {
      const response = await api.post(
        `/api/v1/reconciliation/export-exhibit-package/${reportId}`,
        {
          bates_prefix: batesPrefix || 'CLM-',
          bates_start: Math.max(1, parseInt(batesStart, 10) || 1),
          include_workpaper: batesIncludeWorkpaper,
        },
      );
      const blob =
        response instanceof Blob
          ? response
          : new Blob([response as any], { type: 'application/zip' });

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const today = new Date().toISOString().split('T')[0];
      link.download = `ExhibitPackage_${reportId!.slice(0, 8)}_${today}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      setBatesStatus({ status: 'done' });
    } catch (err: any) {
      setBatesStatus({
        status: 'error',
        error: err?.message || 'Bates package build failed.',
      });
    }
  }

  async function handleDownload(card: ExportCard) {
    setCardState((s) => ({ ...s, [card.id]: { status: 'downloading' } }));
    try {
      // `api.get` auto-detects content-type: it returns a Blob for
      // XLSX / octet-stream / PDF, and a parsed object for JSON. We
      // normalize both into a Blob so the Save-As flow is identical.
      const response = await api.get(effectiveUrl(card));

      const blob =
        response instanceof Blob
          ? response
          : new Blob([JSON.stringify(response, null, 2)], { type: card.mime });

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filenameFor(card, reportId!, effectiveExtension(card));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      setCardState((s) => ({ ...s, [card.id]: { status: 'done' } }));
    } catch (err: any) {
      // api.ts throws `Error(errorMessage)` on non-OK; the surfaced
      // message already includes the HTTP status. A 404 from these
      // endpoints means the case isn't ready to export yet.
      const raw = String(err?.message || '');
      const detail =
        raw.includes('404') || raw.toLowerCase().includes('not found')
          ? 'Not available yet. The case must have processed transactions before this export is generated.'
          : raw || 'Download failed.';
      setCardState((s) => ({
        ...s,
        [card.id]: { status: 'error', error: detail },
      }));
    }
  }

  return (
    <AccountantLayout>
      <div className="p-6 max-w-5xl mx-auto" style={{ background: 'var(--color-bg-base)' }}>
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/accountant/reconciliation/${reportId}`)}
            className="cs-meta flex items-center gap-1.5 mb-3 transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
          >
            <ArrowLeft size={16} />
            Back to reconciliation
          </button>
          <h1 className="cs-display">
            Export Center
          </h1>
          <p className="cs-meta mt-1">
            Every per-case deliverable in one place. Report{' '}
            <code
              className="cs-figure px-1.5 py-0.5 rounded"
              style={{ background: 'var(--color-bg-elevated)' }}
            >
              {reportId.slice(0, 8)}
            </code>
            .
          </p>
        </div>

        {/* Filter controls — Task 9. Only affect the Reconciliation
            Workpaper card; the others are whole-case by design. */}
        <div
          className="rounded-xl p-4 mb-4"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          data-testid="reconciliation-filter-panel"
        >
          <div className="cs-body flex items-center gap-2 mb-3" style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>
            <Filter size={14} />
            Reconciliation Workpaper filters
            {filtersActive && (
              <span
                data-testid="filters-active-badge"
                className="cs-chip ml-2 px-2 py-0.5 rounded-full"
                style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
              >
                scoped
              </span>
            )}
          </div>
          <p className="cs-meta mb-3">
            Generates a focused workpaper suitable for court exhibit
            submission. Leave blank for the full 6-sheet workbook.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <label className="block">
              <span className="cs-label block mb-1">Year</span>
              <input
                type="number"
                min="2000"
                max="2100"
                value={filters.year}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, year: e.target.value }))
                }
                placeholder="e.g. 2023"
                data-testid="filter-year"
                className="cs-body w-full rounded px-2 py-1.5 focus:outline-none"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              />
            </label>
            <label className="block">
              <span className="cs-label block mb-1">Month</span>
              <select
                value={filters.month}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, month: e.target.value }))
                }
                data-testid="filter-month"
                className="cs-body w-full rounded px-2 py-1.5 focus:outline-none"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              >
                <option value="">All</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="cs-label block mb-1">
                Line item
              </span>
              <input
                type="text"
                value={filters.lineItem}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, lineItem: e.target.value }))
                }
                placeholder="e.g. Gross Sales"
                data-testid="filter-line-item"
                className="cs-body w-full rounded px-2 py-1.5 focus:outline-none"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              />
            </label>
            <label className="block">
              <span className="cs-label block mb-1">Source</span>
              <select
                value={filters.source}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, source: e.target.value }))
                }
                data-testid="filter-source"
                className="cs-body w-full rounded px-2 py-1.5 focus:outline-none"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              >
                {SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="cs-label block mb-1">Format</span>
              <select
                value={filters.format}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    format: e.target.value as 'xlsx' | 'csv',
                  }))
                }
                data-testid="filter-format"
                className="cs-body w-full rounded px-2 py-1.5 focus:outline-none"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              >
                <option value="xlsx">XLSX (6 sheets)</option>
                <option value="csv">CSV (transactions)</option>
              </select>
            </label>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {EXPORT_CARDS.map((card) => {
            const state = cardState[card.id];
            return (
              <div
                key={card.id}
                data-testid={`export-card-${card.id}`}
                className="rounded-xl p-5 flex flex-col"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="rounded-lg p-2"
                    style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                  >
                    {card.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="cs-h3">
                      {card.title}
                    </h2>
                    <p className="cs-meta mt-0.5">
                      {card.format}
                    </p>
                  </div>
                </div>

                <p className="cs-body flex-1">
                  {card.description}
                </p>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <button
                    onClick={() => handleDownload(card)}
                    disabled={state.status === 'downloading'}
                    data-testid={`download-${card.id}`}
                    className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors disabled:cursor-not-allowed"
                    style={{
                      fontWeight: 500,
                      background: 'transparent',
                      borderColor: state.status === 'downloading' ? 'var(--color-border)' : 'var(--color-brand)',
                      color: state.status === 'downloading' ? 'var(--color-text-muted)' : 'var(--color-needs-human-text)',
                    }}
                    onMouseEnter={(e) => {
                      if (state.status !== 'downloading') e.currentTarget.style.background = 'var(--color-needs-human-tint)';
                    }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {state.status === 'downloading' ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Generating…
                      </>
                    ) : (
                      <>
                        <Download size={16} />
                        Download
                      </>
                    )}
                  </button>

                  {state.status === 'done' && (
                    <span className="cs-meta" style={{ color: 'var(--color-verified)' }}>
                      Downloaded ✓
                    </span>
                  )}
                </div>

                {state.status === 'error' && (
                  <div
                    role="alert"
                    data-testid={`error-${card.id}`}
                    className="cs-meta mt-3 rounded-md p-2"
                    style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
                  >
                    {state.error}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="cs-meta mt-8 mb-6" style={{ color: 'var(--color-text-dim)' }}>
          <FileText className="inline mr-1" size={12} />
          Every file is regenerated from the current reconciliation state
          each time you click Download. No stale copies.
        </p>

        {/* Bates-indexed exhibit package — Task 10. Separate section
            because it's a POST with a body + a larger build time, not
            a one-click GET like the cards above. */}
        <div
          className="rounded-xl p-5"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          data-testid="bates-package-section"
        >
          <div className="flex items-start gap-3 mb-3">
            <div
              className="rounded-lg p-2"
              style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            >
              <Archive size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="cs-h3">
                Bates-Indexed Exhibit Package
              </h2>
              <p className="cs-meta mt-0.5">
                ZIP · stamped PDFs + Document Index + README
              </p>
            </div>
          </div>
          <p className="cs-body mb-3">
            Court-production ZIP. Every uploaded PDF is stamped with a
            unique Bates number in the bottom-right; the Document
            Index XLSX maps each file to its Bates range.{' '}
            <strong style={{ color: 'var(--color-text-primary)' }}>
              This can take a minute for large claims.
            </strong>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <label className="block">
              <span className="cs-label block mb-1">
                Bates prefix
              </span>
              <input
                type="text"
                value={batesPrefix}
                onChange={(e) => setBatesPrefix(e.target.value)}
                data-testid="bates-prefix"
                className="cs-figure w-full rounded px-2 py-1.5 focus:outline-none"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              />
            </label>
            <label className="block">
              <span className="cs-label block mb-1">
                Starting number
              </span>
              <input
                type="number"
                min="1"
                value={batesStart}
                onChange={(e) => setBatesStart(e.target.value)}
                data-testid="bates-start"
                className="cs-figure w-full rounded px-2 py-1.5 focus:outline-none"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
              />
            </label>
            <label className="cs-body flex items-center gap-2 mt-6" style={{ color: 'var(--color-text-secondary)' }}>
              <input
                type="checkbox"
                checked={batesIncludeWorkpaper}
                onChange={(e) => setBatesIncludeWorkpaper(e.target.checked)}
                data-testid="bates-include-workpaper"
              />
              Include reconciliation workpaper
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleBatesPackage}
              disabled={batesStatus.status === 'downloading'}
              data-testid="build-bates-package"
              className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors disabled:cursor-not-allowed"
              style={{
                fontWeight: 500,
                background: 'transparent',
                borderColor: batesStatus.status === 'downloading' ? 'var(--color-border)' : 'var(--color-brand)',
                color: batesStatus.status === 'downloading' ? 'var(--color-text-muted)' : 'var(--color-needs-human-text)',
              }}
              onMouseEnter={(e) => {
                if (batesStatus.status !== 'downloading') e.currentTarget.style.background = 'var(--color-needs-human-tint)';
              }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {batesStatus.status === 'downloading' ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Building ZIP…
                </>
              ) : (
                <>
                  <Download size={16} />
                  Build exhibit package
                </>
              )}
            </button>
            {batesStatus.status === 'done' && (
              <span className="cs-meta" style={{ color: 'var(--color-verified)' }}>Downloaded ✓</span>
            )}
          </div>

          {batesStatus.status === 'error' && (
            <div
              role="alert"
              data-testid="bates-error"
              className="cs-meta mt-3 rounded-md p-2"
              style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
            >
              {batesStatus.error}
            </div>
          )}
        </div>
      </div>
    </AccountantLayout>
  );
}
